import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import { applyTheme, getStoredTheme, resolveTheme, type Theme } from './theme';

export type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
  theme: Theme;
  resolved: ResolvedTheme;
  setTheme: (t: Theme) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getStoredTheme());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveTheme(getStoredTheme()));

  // Sync DOM class whenever theme changes (applyTheme is idempotent; also covers initial mount)
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Subscribe to system preference changes only when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') return;

    const mql = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (_e: MediaQueryListEvent) => {
      applyTheme('system');
      setResolved(resolveTheme('system'));
    };

    mql.addEventListener('change', handleChange);
    return () => {
      mql.removeEventListener('change', handleChange);
    };
  }, [theme]);

  const setTheme = (next: Theme) => {
    applyTheme(next);
    setThemeState(next);
    setResolved(resolveTheme(next));
  };

  return (
    <ThemeContext.Provider value={{ theme, resolved, setTheme }}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
